import IconInbox from '../../../../components/Icon/IconInbox';
import IconTag from '../../../../components/Icon/IconTag';
import IconCreditCard from '../../../../components/Icon/IconCreditCard';
import { IconClock24 } from '@tabler/icons-react';

function UserLevelSummaryComp({ attendanceData, weeklyIdleMinutes, totalHoursSpent }) {
  const iconMap = {
    attendance_percentage: IconInbox,
    total_attendance_days: IconTag,
    total_leaves_current_month: IconCreditCard,
    data1: IconInbox,
    data2: IconTag,
  };

  // Calculate percentage values (kept unchanged)
  const weeklyIdleMinutesPercentage = weeklyIdleMinutes ? ((weeklyIdleMinutes / 100) * 100).toFixed(2) : 0;
  const totalHoursSpentPercentage = totalHoursSpent ? ((totalHoursSpent / 100) * 100).toFixed(2) : 0;
  const attendancePercentage = attendanceData?.attendance_percentage
    ? Number(parseFloat(attendanceData.attendance_percentage).toFixed(2))
    : 0;
  const totalAttendanceDaysPercentage = attendanceData?.total_attendance_days
    ? ((attendanceData.total_attendance_days / 100) * 100).toFixed(2)
    : 0;
  const totalLeavesPercentage = attendanceData?.total_leaves_current_month
    ? ((attendanceData.total_leaves_current_month / 100) * 100).toFixed(2)
    : 0;

  // Construct formattedData with precomputed percentages
  const formattedData = {
    attendance_percentage: [{
      label: "MTD Attendance Percentage",
      value: attendancePercentage || 0,
      percentage: attendancePercentage,
    }],
    total_attendance_days: [{
      label: "MTD Total Attendance Days",
      value: attendanceData?.total_attendance_days || 0,
      percentage: totalAttendanceDaysPercentage,
    }],
    total_leaves_current_month: [{
      label: "MTD Total Leaves",
      value: attendanceData?.total_leaves_current_month || 0,
      percentage: totalLeavesPercentage,
    }],
    data1: [{
      label: "Total Weekly Idle Minutes",
      value: weeklyIdleMinutes || 0,
      percentage: weeklyIdleMinutesPercentage,
    }],
    data2: [{
      label: "Total Hours Spent",
      value: totalHoursSpent || 0,
      percentage: totalHoursSpentPercentage,
    }],
  };

  // Keys for each section
  const attendanceKeys = ["attendance_percentage", "total_attendance_days", "total_leaves_current_month"];
  const timeKeys = ["data1", "data2"];

  // Get gradient classes for Attendance metrics
  const getAttendanceGradient = (key, percentage, value) => {
    if (key === "attendance_percentage") {
      const p = parseFloat(percentage);
      if (p >= 80) return "bg-gradient-to-r from-green-400 to-green-600";
      else if (p >= 50) return "bg-gradient-to-r from-orange-400 to-orange-600";
      else return "bg-gradient-to-r from-red-400 to-red-600";
    } else if (key === "total_attendance_days") {
      // Default blue gradient for attendance days
      return "bg-gradient-to-r from-blue-400 to-blue-600";
    } else if (key === "total_leaves_current_month") {
      // If leave count greater than 2 then red, else blue gradient
      return (parseInt(value) > 2)
        ? "bg-gradient-to-r from-red-400 to-red-600"
        : "bg-gradient-to-r from-blue-400 to-blue-600";
    }
    return "bg-blue-500"; // fallback
  };

  // Render each attendance metric row with a gradient progress bar
  const renderAttendanceMetric = (key) => {
    const item = formattedData[key][0];
    const IconComponent = iconMap[key];
    const gradientClass = getAttendanceGradient(key, item.percentage, item.value);
    return (
      <div key={key} className="flex items-center my-4">
        <div className="flex-shrink-0">
          <div className="bg-gray-100 rounded-full p-3 mr-4 shadow-lg">
            <IconComponent className="h-6 w-6 text-gray-700" />
          </div>
        </div>
        <div className="flex-grow">
          <div className="flex justify-between items-center mb-1">
            <span className="text-gray-800 font-medium">{item.label}</span>
            <span className="text-gray-900 font-bold">{item.value}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all duration-500 ease-out ${gradientClass}`}
              style={{ width: `${Math.min(item.percentage, 100)}%` }}
            ></div>
          </div>
        </div>
      </div>
    );
  };

  // Render a time metric as an individual styled card (showing value only)
  const renderTimeMetric = (key) => {
    const item = formattedData[key][0];
    const IconComponent = iconMap[key];
    return (
      <div key={key} className="bg-[#19294F] text-white p-6 rounded-xl shadow-xl transform transition hover:scale-105 h-40">
        <div className="flex items-center h-full">
          <div className="bg-white rounded-full p-3 mr-4">
            <IconComponent className="h-8 w-8 text-[#19294F]" />
          </div>
          <div className="flex flex-col justify-center">
            <h4 className="text-lg font-semibold">{item.label}</h4>
            <p className="text-3xl font-bold">{item.value}</p>
          </div>
        </div>
      </div>
    );
  };

return (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
    {/* Attendance Overview Card */}
    <div className="bg-white shadow-xl rounded-xl p-6 border-t-4 border-[#EE1D23] hover:shadow-2xl transition-transform duration-300 hover:scale-[1.02]">
      <h3 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-2">Attendance Overview</h3>
      {attendanceKeys.map(key => renderAttendanceMetric(key))}
    </div>

    {/* Time Spent Overview Card */}
    <div className="bg-white shadow-xl rounded-xl p-6 border-t-4 border-[#1A2B5C] hover:shadow-2xl transition-transform duration-300 hover:scale-[1.02]">
      <h3 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-2">Time Spent Overview</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {timeKeys.map(key => renderTimeMetric(key))}
      </div>
    </div>
  </div>
);

}

export default UserLevelSummaryComp;
